import { inject, Injectable } from '@angular/core';
import {
    IChecklistTimeInterval,
    IChecklistTimeIntervalData,
    IChecklistTimeIntervalGroup,
} from '@fi-shared/checklist/checklist-v2';
import { FiChecklistTimeIntervalType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-type';
import { FiChecklistTimeIntervalDataType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-data-type';
import { FiLocaleService } from '@fi-shared/fi-locale.service';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';

export interface IAuditOnDemandOption {
    intervalDataKey: string;
    labelPlural: string;
    labelSingle: string;
    timeMs: number;
}

@Injectable()
export class FiChecklist2ScheduleIntervalService {
    readonly allRecurringTimeIntervals: ReadonlyArray<FiChecklistTimeIntervalType> = [
        FiChecklistTimeIntervalType.RecurringDaily,
        FiChecklistTimeIntervalType.RecurringWeekly,
        FiChecklistTimeIntervalType.RecurringMonthly,
        FiChecklistTimeIntervalType.RecurringYearly,
    ];
    readonly auditOnDemandOptions: ReadonlyArray<IAuditOnDemandOption> = [
        { intervalDataKey: 'Year', labelPlural: 'common.years', labelSingle: 'common.year', timeMs: 31556952000 },
        { intervalDataKey: 'Month', labelPlural: 'common.months', labelSingle: 'common.month', timeMs: 2629746000 },
        { intervalDataKey: 'Week', labelPlural: 'common.weeks', labelSingle: 'common.week', timeMs: 604800000 },
        { intervalDataKey: 'Day', labelPlural: 'common.days', labelSingle: 'common.day', timeMs: 86400000 },
        { intervalDataKey: 'Hour', labelPlural: 'common.hours', labelSingle: 'hour', timeMs: 3600000 },
        { intervalDataKey: 'Minute', labelPlural: 'common.minutes', labelSingle: 'common.minute', timeMs: 60000 },
    ];
    // https://chainformation.atlassian.net/wiki/spaces/FI/pages/2224422913/Recurring+daily
    readonly intervalMinGap: number = 29;

    private readonly defaultInterval: IChecklistTimeInterval = {
        Start: { Hour: undefined, Minute: undefined },
        End: { Hour: undefined, Minute: undefined },
        IsEnabled: true,
    };
    private readonly defaultWeeklyInterval: IChecklistTimeInterval = {
        Start: { DayOfWeek: 1, ...this.defaultInterval.Start },
        End: { DayOfWeek: 7, ...this.defaultInterval.End },
        IsEnabled: true,
    };
    private readonly defaultYearlyInterval: IChecklistTimeInterval = {
        Start: { Month: 1, Day: 1, ...this.defaultInterval.Start },
        End: { Month: 12, Day: 31, ...this.defaultInterval.End },
        IsEnabled: true,
    };

    private fiLocaleService = inject(FiLocaleService);

    someIntervalsConflicted(intervals: IChecklistTimeInterval[], intervalType: FiChecklistTimeIntervalType): boolean {
        return !!this.getFirstConflictedTimeInterval(intervals, intervalType);
    }

    getConflictedAndUnsetTimeIntervals(
        intervals: IChecklistTimeInterval[],
        intervalType: FiChecklistTimeIntervalType,
    ): {
        conflictedInterval: IChecklistTimeInterval | undefined;
        unsetInterval: IChecklistTimeInterval | undefined;
    } {
        return {
            conflictedInterval: this.getFirstConflictedTimeInterval(intervals, intervalType),
            unsetInterval: this.getFirstUnsetTimeInterval(intervals),
        };
    }

    getFirstConflictedTimeInterval(
        intervals: IChecklistTimeInterval[],
        intervalType: FiChecklistTimeIntervalType,
    ): IChecklistTimeInterval | undefined {
        if (intervals.length === 1) {
            return undefined;
        }

        for (let i = 0; i < intervals.length; i++) {
            const interval1 = intervals[i];
            const conflictedInterval = intervals
                .slice(i + 1)
                .find((interval2) => this.areTimeIntervalsConflicted(interval1, interval2, intervalType));

            if (conflictedInterval) {
                return conflictedInterval;
            }
        }

        return undefined;
    }

    someTimeIntervalsConflictedWithPreviousDailyGroup(
        intervalGroup: IChecklistTimeIntervalGroup,
        intervalType: FiChecklistTimeIntervalType,
    ): boolean {
        if (!this.isDailyInterval(intervalType)) {
            return false;
        }

        return Object.keys(intervalGroup).some((key: string) => {
            return !!this.getFirstTimeIntervalConflictedWithPreviousDailyGroup(intervalGroup, key, intervalType);
        });
    }

    getFirstTimeIntervalConflictedWithPreviousDailyGroup(
        intervalGroup: IChecklistTimeIntervalGroup,
        scheduleTimeIntervalKey: string,
        intervalType: FiChecklistTimeIntervalType,
    ): IChecklistTimeInterval | undefined {
        if (!this.isDailyInterval(intervalType)) {
            return undefined;
        }

        const previousGroupKey = this.getPreviousTimeIntervalGroupKey(intervalGroup, scheduleTimeIntervalKey);
        const previousGroup = intervalGroup[previousGroupKey];
        const currentGroup = intervalGroup[scheduleTimeIntervalKey];

        if (!previousGroup?.length || !currentGroup?.length) {
            return undefined;
        }

        const previousGroupLatestEndMinute = this.getLatestDailyEndMinute(previousGroup);

        if (!isDefined(previousGroupLatestEndMinute)) {
            return undefined;
        }

        return currentGroup
            .filter((interval: IChecklistTimeInterval) => {
                return interval.IsEnabled && this.isHourMinuteDataSet(interval.Start);
            })
            .find((interval: IChecklistTimeInterval) => {
                const startMinuteFromPreviousDay = 24 * 60 + this.convertIntervalTimeToMinute(interval.Start);

                return previousGroupLatestEndMinute + this.intervalMinGap >= startMinuteFromPreviousDay;
            });
    }

    getFirstUnsetTimeInterval(intervals: IChecklistTimeInterval[]): IChecklistTimeInterval | undefined {
        return intervals.find((interval) => {
            return (
                (isNullOrUndefined(interval.Start.Hour) ||
                    isNullOrUndefined(interval.Start.Minute) ||
                    isNullOrUndefined(interval.End.Hour) ||
                    isNullOrUndefined(interval.End.Minute)) &&
                interval.IsEnabled
            );
        });
    }

    isAtLeastOneIntervalTimeDataFilled(intervalGroup: IChecklistTimeIntervalGroup): boolean {
        return Object.keys(intervalGroup).some((key) => {
            return intervalGroup[key].some((interval: IChecklistTimeInterval) => {
                return (
                    (!isNullOrUndefined(interval.Start.Hour) ||
                        !isNullOrUndefined(interval.Start.Minute) ||
                        !isNullOrUndefined(interval.End.Hour) ||
                        !isNullOrUndefined(interval.End.Minute)) &&
                    interval.IsEnabled
                );
            });
        });
    }

    private getNewWeeklyInterval(): IChecklistTimeInterval {
        const weeklyInterval: IChecklistTimeInterval = cloneDeep(this.defaultWeeklyInterval);
        weeklyInterval.Start.DayOfWeek = undefined;
        weeklyInterval.End.DayOfWeek = undefined;
        return weeklyInterval;
    }

    private getNewYearlyInterval(): IChecklistTimeInterval {
        const yearlyInterval: IChecklistTimeInterval = cloneDeep(this.defaultYearlyInterval);
        yearlyInterval.Start.Month = undefined;
        yearlyInterval.End.Month = undefined;
        yearlyInterval.Start.Day = undefined;
        yearlyInterval.End.Day = undefined;
        return yearlyInterval;
    }

    getDefaultTimeIntervalByType(
        timeIntervalType: FiChecklistTimeIntervalType,
        additionalData: number,
    ): IChecklistTimeInterval {
        switch (timeIntervalType) {
            case FiChecklistTimeIntervalType.RecurringDaily:
                return this.getNewDailyInterval(additionalData);
            case FiChecklistTimeIntervalType.RecurringMonthly:
                return this.getNewMonthInterval(additionalData, true);
            case FiChecklistTimeIntervalType.RecurringWeekly:
                return this.getNewWeeklyInterval();
            case FiChecklistTimeIntervalType.RecurringYearly:
                return this.getNewYearlyInterval();
        }
    }

    getDefaultTimeIntervals(timeIntervalType: FiChecklistTimeIntervalType): IChecklistTimeInterval[] {
        const timeIntervals: IChecklistTimeInterval[] = [];
        if (this.isDailyInterval(timeIntervalType)) {
            for (let weekDay = 1; weekDay <= 7; weekDay++) {
                timeIntervals.push(this.getNewDailyInterval(weekDay));
            }
        } else if (this.isWeeklyInterval(timeIntervalType)) {
            timeIntervals.push(cloneDeep(this.defaultWeeklyInterval));
        } else if (this.isMonthlyInterval(timeIntervalType)) {
            for (let month = 1; month <= 12; month++) {
                timeIntervals.push(this.getNewMonthInterval(month));
            }
        } else if (this.isYearlyInterval(timeIntervalType)) {
            timeIntervals.push(cloneDeep(this.defaultYearlyInterval));
        } else if (this.isOneTimeSchedule(timeIntervalType)) {
            const date: Date = new Date();
            date.setDate(date.getDate() + 1); // tomorrow date
            const Start: IChecklistTimeIntervalData = {
                Year: date.getFullYear(),
                Month: date.getMonth() + 1,
                Day: date.getDate(),
                Hour: 8,
                Minute: 0,
            };
            date.setDate(date.getDate() + 1); // the day after tomorrow date
            const End: IChecklistTimeIntervalData = {
                Year: date.getFullYear(),
                Month: date.getMonth() + 1,
                Day: date.getDate(),
                Hour: 20,
                Minute: 0,
            };
            timeIntervals.push({ Start, End, IsEnabled: true });
        } else if (
            timeIntervalType === FiChecklistTimeIntervalType.OnDemand ||
            timeIntervalType === FiChecklistTimeIntervalType.Audit
        ) {
            timeIntervals.push({
                Start: {},
                End: { Hour: 8 },
                IsEnabled: true,
            });
        }
        return timeIntervals;
    }

    hasExtraIntervals(intervalType: FiChecklistTimeIntervalType): boolean {
        return this.isDailyInterval(intervalType) || this.isMonthlyInterval(intervalType);
    }

    isDailyInterval(intervalType: FiChecklistTimeIntervalType): boolean {
        return intervalType === FiChecklistTimeIntervalType.RecurringDaily;
    }

    isWeeklyInterval(intervalType: FiChecklistTimeIntervalType): boolean {
        return intervalType === FiChecklistTimeIntervalType.RecurringWeekly;
    }

    isMonthlyInterval(intervalType: FiChecklistTimeIntervalType): boolean {
        return intervalType === FiChecklistTimeIntervalType.RecurringMonthly;
    }

    isYearlyInterval(intervalType: FiChecklistTimeIntervalType): boolean {
        return intervalType === FiChecklistTimeIntervalType.RecurringYearly;
    }

    isAuditOrOnDemandInterval(intervalType: FiChecklistTimeIntervalType): boolean {
        return this.isAuditInterval(intervalType) || this.isOnDemandInterval(intervalType);
    }

    isOneTimeSchedule(intervalType: FiChecklistTimeIntervalType): boolean {
        return intervalType === FiChecklistTimeIntervalType.OneTime;
    }

    isAuditInterval(intervalType: FiChecklistTimeIntervalType): boolean {
        return intervalType === FiChecklistTimeIntervalType.Audit;
    }

    isOnDemandInterval(intervalType: FiChecklistTimeIntervalType): boolean {
        return intervalType === FiChecklistTimeIntervalType.OnDemand;
    }

    groupTimeIntervals(
        intervals: IChecklistTimeInterval[],
        timeIntervalType: FiChecklistTimeIntervalType,
    ): IChecklistTimeIntervalGroup {
        const intervalGroup: IChecklistTimeIntervalGroup = {};
        intervals.forEach((interval, index) => {
            if (this.isDailyInterval(timeIntervalType)) {
                if (intervalGroup.hasOwnProperty(interval.Start.DayOfWeek)) {
                    intervalGroup[interval.Start.DayOfWeek].push(interval);
                } else {
                    intervalGroup[interval.Start.DayOfWeek] = [interval];
                }
            } else if (this.isMonthlyInterval(timeIntervalType)) {
                if (intervalGroup.hasOwnProperty(interval.Start.Month)) {
                    intervalGroup[interval.Start.Month].push(interval);
                } else {
                    intervalGroup[interval.Start.Month] = [interval];
                }
            } else {
                intervalGroup[index] = [interval];
            }
        });
        return intervalGroup;
    }

    getIntervalsFromGroup(group: IChecklistTimeIntervalGroup): IChecklistTimeInterval[] {
        return Object.keys(group).flatMap((key) => [...group[key]]);
    }

    getSortedTimeIntervalGroup(
        timeIntervalGroup: IChecklistTimeIntervalGroup,
        timeIntervalType: FiChecklistTimeIntervalType,
    ): IChecklistTimeIntervalGroup {
        const sortedTimeIntervalGroup: IChecklistTimeIntervalGroup = {};

        if (this.hasExtraIntervals(timeIntervalType)) {
            Object.keys(timeIntervalGroup).forEach((key: string) => {
                sortedTimeIntervalGroup[Number(key)] = this.getSortedTimeIntervals(timeIntervalGroup[key]);
            });
        } else {
            Object.keys(timeIntervalGroup)
                .map((key: string) => this.getSortedTimeIntervals(timeIntervalGroup[key]))
                .sort((intervalsA, intervalsB) => this.compareTimeIntervalStart(intervalsA[0], intervalsB[0]))
                .forEach((intervals: IChecklistTimeInterval[], index: number) => {
                    sortedTimeIntervalGroup[index] = intervals;
                });
        }

        return sortedTimeIntervalGroup;
    }

    convertIntervalDataToMs(intervalData: IChecklistTimeIntervalData): number {
        const intervalDataKey: string = Object.keys(intervalData).find((key) => isDefined(intervalData[key]));
        const selectedOption: IAuditOnDemandOption = this.auditOnDemandOptions.find((option: IAuditOnDemandOption) => {
            return option.intervalDataKey === intervalDataKey;
        });
        return intervalData[intervalDataKey] * selectedOption.timeMs;
    }

    convertIntervalTimeDateToDate(timeIntervalData: IChecklistTimeIntervalData): Date {
        return new Date(
            timeIntervalData.Year,
            timeIntervalData.Month - 1,
            timeIntervalData.Day,
            timeIntervalData.Hour,
            timeIntervalData.Minute,
        );
    }

    convertDateToIntervalTimeData(date: Date): IChecklistTimeIntervalData {
        return {
            Year: date.getFullYear(),
            Month: date.getMonth() + 1,
            Day: date.getDate(),
            Hour: date.getHours(),
            Minute: date.getMinutes(),
        };
    }

    getDateWithGapByInterval(baseIntervalData: IChecklistTimeIntervalData): Date {
        return new Date(
            this.convertIntervalTimeDateToDate(baseIntervalData).getTime() + (this.intervalMinGap + 1) * 60000,
        );
    }

    getAuditOnDemandEndDate(timeInterval: IChecklistTimeInterval): Date {
        const baseDate: Date = this.convertIntervalTimeDateToDate(timeInterval.Start);
        if (isDefined(timeInterval.End.Year)) {
            baseDate.setFullYear(baseDate.getFullYear() + timeInterval.End.Year);
        }
        if (isDefined(timeInterval.End.Month)) {
            baseDate.setMonth(baseDate.getMonth() + 1 + timeInterval.End.Month + 1);
        }
        if (isDefined(timeInterval.End.Day)) {
            baseDate.setDate(baseDate.getDate() + timeInterval.End.Day);
        }
        if (isDefined(timeInterval.End.Hour)) {
            baseDate.setHours(baseDate.getHours() + timeInterval.End.Hour);
        }
        if (isDefined(timeInterval.End.Minute)) {
            baseDate.setMinutes(baseDate.getMinutes() + timeInterval.End.Minute);
        }
        return baseDate;
    }

    isIntervalDataMissing(intervalGroup: IChecklistTimeIntervalGroup): boolean {
        return Object.keys(intervalGroup).some((key) => {
            if (intervalGroup[key].length === 0) {
                return true;
            }
            return intervalGroup[key].some((interval: IChecklistTimeInterval) => {
                if (!interval.IsEnabled) {
                    return false;
                }
                const isIntervalStartTimeMissing = Object.keys(interval.Start).some((intervalStartTimeKey) => {
                    return interval.Start[intervalStartTimeKey] === undefined;
                });
                const isIntervalEndTimeMissing = Object.keys(interval.End).some((intervalEndTimeKey) => {
                    return interval.End[intervalEndTimeKey] === undefined;
                });
                return isIntervalStartTimeMissing || isIntervalEndTimeMissing;
            });
        });
    }

    private areTimeIntervalsConflicted(
        interval1: IChecklistTimeInterval,
        interval2: IChecklistTimeInterval,
        intervalType: FiChecklistTimeIntervalType,
    ): boolean {
        let start1 = this.convertIntervalTimeToMinute(interval1.Start);
        let end1 = this.convertIntervalTimeToMinute(interval1.End);
        let start2 = this.convertIntervalTimeToMinute(interval2.Start);
        let end2 = this.convertIntervalTimeToMinute(interval2.End);

        const isWeeklyInterval = this.isWeeklyInterval(intervalType);
        const isDailyInterval = this.isDailyInterval(intervalType);

        if (isWeeklyInterval) {
            start1 += this.convertIntervalWeekDayToMinute(interval1.Start);
            end1 += this.convertIntervalWeekDayToMinute(interval1.End);
            start2 += this.convertIntervalWeekDayToMinute(interval2.Start);
            end2 += this.convertIntervalWeekDayToMinute(interval2.End);
        }
        if (isDailyInterval) {
            end1 += this.convertIntervalNextDayToMinute(interval1.End);
            end2 += this.convertIntervalNextDayToMinute(interval2.End);
        }

        const isTimeOverlap = end1 + this.intervalMinGap >= start2 && start1 <= end2 + this.intervalMinGap;

        if (isDailyInterval || isWeeklyInterval) {
            return isTimeOverlap;
        }

        if (this.isMonthlyInterval(intervalType)) {
            const firstIntervalStartInDays: number = this.getDayByDataType(interval1.Start);
            const firstIntervalEndInDays: number = this.getDayByDataType(interval1.End);
            const secondIntervalStartInDays: number = this.getDayByDataType(interval2.Start);
            const secondIntervalEndInDays: number = this.getDayByDataType(interval2.End);

            const isMonthDayOverlap: boolean =
                firstIntervalEndInDays >= secondIntervalStartInDays &&
                firstIntervalStartInDays < secondIntervalEndInDays;

            return isMonthDayOverlap && isTimeOverlap;
        }

        const isDaysOverlap = interval1.End.Day >= interval2.Start.Day && interval1.Start.Day < interval2.End.Day;

        const isMonthsOverlap =
            interval1.End.Month >= interval2.Start.Month && interval1.Start.Month < interval2.End.Month;

        return isMonthsOverlap && isDaysOverlap && isTimeOverlap;
    }

    private getPreviousTimeIntervalGroupKey(
        intervalGroup: IChecklistTimeIntervalGroup,
        scheduleTimeIntervalKey: string,
    ): string {
        const intervalGroupKeys = Object.keys(intervalGroup);
        const currentGroupIndex = intervalGroupKeys.indexOf(scheduleTimeIntervalKey);

        return currentGroupIndex === 0
            ? intervalGroupKeys[intervalGroupKeys.length - 1]
            : intervalGroupKeys[currentGroupIndex - 1];
    }

    private getLatestDailyEndMinute(intervals: IChecklistTimeInterval[]): number | undefined {
        return intervals
            .filter((interval: IChecklistTimeInterval) => {
                return interval.IsEnabled && this.isHourMinuteDataSet(interval.End);
            })
            .map((interval: IChecklistTimeInterval) => {
                return this.convertIntervalTimeToMinute(interval.End) + this.convertIntervalNextDayToMinute(interval.End);
            })
            .reduce((latestEndMinute: number | undefined, endMinute: number) => {
                return !isDefined(latestEndMinute) || endMinute > latestEndMinute ? endMinute : latestEndMinute;
            }, undefined);
    }

    private isHourMinuteDataSet(intervalData: IChecklistTimeIntervalData): boolean {
        return isDefined(intervalData.Hour) && isDefined(intervalData.Minute);
    }

    private getNewMonthInterval(month: number, isEmpty?: boolean): IChecklistTimeInterval {
        const interval: IChecklistTimeInterval = cloneDeep(this.defaultInterval);
        interval.Start.Month = month;
        interval.Start.Day = isEmpty ? undefined : 1;
        interval.Start.DataType = FiChecklistTimeIntervalDataType.Day;
        interval.End.Month = month;
        interval.End.DataType = isEmpty ? undefined : FiChecklistTimeIntervalDataType.LastDay;
        return interval;
    }

    private getNewDailyInterval(weekDay: number): IChecklistTimeInterval {
        const interval = cloneDeep(this.defaultInterval);
        interval.Start.DayOfWeek = weekDay;
        interval.End.DayOfWeek = weekDay;
        return interval;
    }

    private getDayByDataType(intervalData: IChecklistTimeIntervalData): number {
        switch (intervalData.DataType) {
            case FiChecklistTimeIntervalDataType.Day:
                return intervalData.Day;
            case FiChecklistTimeIntervalDataType.LastDay:
                return this.fiLocaleService.getLastDayInMonth(intervalData.Month);
            case FiChecklistTimeIntervalDataType.FirstWeekDay:
                return this.fiLocaleService.getFirstWeekDayOfMonth(intervalData.Month, intervalData.DayOfWeek);
            case FiChecklistTimeIntervalDataType.LastWeekDay:
                return this.fiLocaleService.getLastWeekdayOfMonth(intervalData.Month, intervalData.DayOfWeek);
        }
    }

    private convertIntervalTimeToMinute(intervalData: IChecklistTimeIntervalData): number {
        return intervalData.Hour * 60 + intervalData.Minute;
    }

    private convertIntervalWeekDayToMinute(intervalData: IChecklistTimeIntervalData): number {
        return intervalData.DayOfWeek * 24 * 60;
    }

    private convertIntervalNextDayToMinute(intervalData: IChecklistTimeIntervalData): number {
        return intervalData.IsNextDay ? 24 * 60 : 0;
    }

    private getSortedTimeIntervals(intervals: IChecklistTimeInterval[]): IChecklistTimeInterval[] {
        return intervals.length > 1 ? [...intervals].sort(this.compareTimeIntervalStart) : [...intervals];
    }

    private compareTimeIntervalStart(intervalA: IChecklistTimeInterval, intervalB: IChecklistTimeInterval): number {
        const intervalStartSortFields = ['Year', 'Month', 'Day', 'DayOfWeek', 'Week', 'Hour', 'Minute', 'DataType'];

        for (const sortField of intervalStartSortFields) {
            const valueA = intervalA.Start[sortField] ?? 0;
            const valueB = intervalB.Start[sortField] ?? 0;

            if (valueA !== valueB) {
                return valueA - valueB;
            }
        }

        return 0;
    }
}
